/**
 * Reusable configuration sections composed by the focused Endpoints,
 * Admin, and My Account pages. The default export is retained only as a
 * compatibility composition for tests and older imports; App routing no
 * longer exposes one mixed-concern Settings page.
 */
import { Switch } from "../components/settings/SectionActions.jsx";
import Notice, { ErrorText, SaveStatus } from "../components/ui/Notice.jsx";
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from "react";
import { restGet, restPost, restDelete, authedFetch } from "../lib/restClient.js";
import AdminViewHeader from "../components/AdminViewHeader.jsx";
import ConfirmDialog from "../components/ConfirmDialog.jsx";
import {
    ComplianceIntegrationHealthProvider,
    useComplianceIntegrationHealth,
} from "../components/ComplianceIntegrationHealth.jsx";
import AccessSummary from "../components/AccessSummary.jsx";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { denialCopy, denialOrRawMessage } from "../lib/denialReason.js";
import { NOTE_CLASS } from "../lib/ui.js";
import Button, { buttonClass } from "../components/ui/Button.jsx";
import Checkbox from "../components/ui/Checkbox.jsx";
import { FIELD } from "../components/ui/styles.js";
import {
    createPerplexityAuditIntegration,
    deletePerplexityAuditIntegration,
    getPerplexityAuditIntegration,
    rotatePerplexityAuditCredential,
} from "../lib/perplexityAuditClient.js";
import {
    createLangSmithIntegration,
    deleteLangSmithIntegration,
    listLangSmithIntegrations,
    listLangSmithProjects,
    setLangSmithWatchedProjects,
} from "../lib/langsmithClient.js";
import {
    DEFAULT_OTEL_COLLECTOR_PORT,
    MAX_OTEL_COLLECTOR_PORT,
    MIN_OTEL_COLLECTOR_PORT,
    parseOtelCollectorPortInput,
    readOtelCollectorPort,
} from "../lib/otelCollectorPort.js";
import {
    getAuthIdentityEpoch,
    getUserMetadata,
    onAuthIdentityChange,
} from "../lib/authTokenStore.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";
import { langsmithIntegration, perplexitySummary, whenLabel } from "./dataSources/model.js";
import {
    complianceHealthPresentation,
    formatComplianceHealthTime,
} from "../lib/complianceIntegrationHealth.js";

// ── Tenant configuration save (shared, non-clobbering) ───────────
//
// The grpc-gateway treats PUT /v1/tenant/configuration as a FULL REPLACE
// of the `configuration` object. Two consequences drive this helper:
//
//   1. Field names MUST be snake_case. The protojson unmarshaler accepts
//      both snake_case and camelCase, but the dashboard convention is to
//      send snake_case to match the proto definitions.
//   2. Every known field must be present on every save or it's wiped.
//      So a section that only knows about the tray toggle cannot PUT just
//      that - it would erase custom_provider_rules etc.
//
// We therefore re-read the live config, rebuild the full known field set
// (defensively reading either casing the GET emits), overlay the caller's
// change, and PUT the complete snake_case payload. `llm_provider` is
// deliberately omitted: the GET never returns the write-only api_key, so
// round-tripping it would clear the key — it's saved on its own path.
function normalizeRules(raw) {
    return (Array.isArray(raw) ? raw : []).map((r) => ({
        id: r.id ?? "",
        provider_tag: r.providerTag ?? r.provider_tag ?? "",
        domain_patterns: r.domainPatterns ?? r.domain_patterns ?? [],
        path_patterns: r.pathPatterns ?? r.path_patterns ?? [],
        method: r.method ?? "CUSTOM_PROVIDER_HTTP_METHOD_UNSPECIFIED",
        strategy: r.strategy ?? "CUSTOM_CAPTURE_STRATEGY_FULL_BODY",
    }));
}

// Exported for the carry-through test: the full-replace PUT is where an
// unrelated save can silently clear a tenant flag this page has no control for.
export async function saveTenantConfiguration(overlay) {
    const current = await restGet("/v1/tenant/configuration");
    const cfg = current?.configuration ?? {};
    const merged = {
        hide_tray_icon: cfg.hideTrayIcon ?? cfg.hide_tray_icon ?? false,
        // Carried on every save (full-replace PUT): a rules-only save must not
        // silently reset the tenant's collector port to the default (O-1709).
        otel_collector_port: readOtelCollectorPort(cfg),
        custom_provider_rules: normalizeRules(
            cfg.customProviderRules ?? cfg.custom_provider_rules,
        ),
        // Caller overrides only the field(s) it manages (snake_case).
        ...overlay,
    };
    // "Unset" (use the default port) travels as an absent field, not an
    // explicit null — sidesteps protojson null-handling for the optional scalar.
    if (merged.otel_collector_port == null) {
        delete merged.otel_collector_port;
    }
    const res = await authedFetch("/v1/tenant/configuration", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ configuration: merged }),
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
            `PUT /v1/tenant/configuration ${res.status}: ${text || res.statusText}`,
        );
    }
    return res.json().catch(() => ({}));
}

// ── Reusable section frame ───────────────────────────────────────

function useLatestRef(value) {
    const ref = useRef(value);
    ref.current = value;
    return ref;
}

function SectionCard({ id, eyebrow, title, description, footer, children, collapsible = false, defaultCollapsed = false, embedded = false }) {
    const [collapsed, setCollapsed] = useState(collapsible ? defaultCollapsed : false);
    const bodyId = id ? `${id}-body` : undefined;
    const headerInner = (
        <>
            {eyebrow ? (
                <span className={EYEBROW}>{eyebrow}</span>
            ) : null}
            <h2 className="m-0 text-[15px] leading-[1.06] font-medium tracking-[-0.04em] text-[color:var(--ink)]">
                {title}
            </h2>
            {description ? (
                <p className={`${NOTE_CLASS} text-[12px] leading-[1.4] mt-[4px]`}>
                    {description}
                </p>
            ) : null}
        </>
    );
    if (embedded) {
        return (
            <section id={id} className="grid gap-[12px] border-t border-[color:var(--line)] pt-[14px] first:border-t-0 first:pt-0">
                <header className="grid gap-[2px]">
                    {eyebrow ? (
                        <span className={EYEBROW}>{eyebrow}</span>
                    ) : null}
                    <h3 className="m-0 text-[13px] leading-[1.1] font-medium tracking-[-0.02em] text-[color:var(--ink)]">
                        {title}
                    </h3>
                    {description ? (
                        <p className={`${NOTE_CLASS} text-[11px] leading-[1.4] mt-[3px]`}>
                            {description}
                        </p>
                    ) : null}
                </header>
                <div className="grid gap-[18px]">{children}</div>
                {footer ? <div className="grid">{footer}</div> : null}
            </section>
        );
    }

    // Body stays mounted when collapsed (visibility toggles via a class, not
    // an unmount) so its data still loads in the background and its aria-busy
    // loading region and aria-controls target remain valid.
    return (
        <section
            id={id}
            className="grid gap-[16px] rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[14px]"
        >
            {collapsible ? (
                // The toggle sits INSIDE the h2, not around it. A <button> takes
                // presentational children in the accessibility tree, so an h2
                // nested in one drops out of the heading list entirely — a
                // screen-reader user navigating Settings by heading finds every
                // other card and not this one. The description stays outside the
                // control so it does not become part of its accessible name.
                <header className="grid gap-[2px]">
                    {eyebrow ? (
                        <span className={EYEBROW}>{eyebrow}</span>
                    ) : null}
                    <h2 className="m-0 text-[15px] leading-[1.06] font-medium tracking-[-0.04em] text-[color:var(--ink)]">
                        <button
                            type="button"
                            onClick={() => setCollapsed((v) => !v)}
                            aria-expanded={!collapsed}
                            aria-controls={bodyId}
                            className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-[8px] m-0 border-0 bg-transparent p-0 text-left font-medium text-[15px] leading-[1.06] tracking-[-0.04em] text-[color:var(--ink)] cursor-pointer outline-none"
                        >
                            <span>{title}</span>
                            <span className="pointer-events-none mt-[2px] inline-flex items-center gap-[5px] text-[color:var(--muted)]">
                                <span className="text-[12px] font-normal tracking-normal">{collapsed ? "Show" : "Hide"}</span>
                                <span aria-hidden="true" className="text-[9px] leading-none text-[color:var(--muted-soft)]">
                                    {collapsed ? "▸" : "▾"}
                                </span>
                            </span>
                        </button>
                    </h2>
                    {description ? (
                        <p className={`${NOTE_CLASS} text-[12px] leading-[1.4] mt-[4px]`}>
                            {description}
                        </p>
                    ) : null}
                </header>
            ) : (
                <header className="grid gap-[2px]">{headerInner}</header>
            )}
            {/* Body uses a generous gap so each FormField / toggle
                row clearly reads as its own unit, not a continuous
                stripe of text. */}
            <div id={bodyId} className={collapsed ? "hidden" : "grid gap-[18px]"}>{children}</div>
            {footer ? <div className={collapsed ? "hidden" : "grid"}>{footer}</div> : null}
        </section>
    );
}

function FormField({ label, hint, children }) {
    return (
        <label className="grid gap-[4px]">
            <span className={NOTE_CLASS}>{label}</span>
            {children}
            {hint ? (
                <span className={`${NOTE_CLASS} text-[10px]`}>{hint}</span>
            ) : null}
        </label>
    );
}

const inputCls = FIELD;

const primaryBtnCls = buttonClass("primary");

const secondaryBtnCls = buttonClass("secondary");

// A card's eyebrow, in sentence case.
const EYEBROW = "text-[12px] leading-[1.2] text-[color:var(--muted)]";

function StatusLine({ status, error, savedHint = "Saved" }) {
    if (status === "saving") {
        return <span className={`${NOTE_CLASS} text-[11px]`}>Saving…</span>;
    }
    if (status === "saved") {
        return <span className="text-[11px] text-[color:var(--green)]">{savedHint}</span>;
    }
    if (error) {
        return <span className="text-[11px] text-red-400">{error}</span>;
    }
    return <span aria-hidden="true">&nbsp;</span>;
}

// ── Endpoint Configuration ───────────────────────────────────────

function defaultEndpointConfig() {
    return {
        hide_tray_icon: false,
        // null = unset → endpoints bind the default port (4318).
        otel_collector_port: null,
    };
}

// The overlay this page sends on save: every field it renders a control for,
// and nothing else (`saveTenantConfiguration` re-reads and preserves the rest).
//
// Exported so a test can assert the payload directly. A control whose state
// this page mutates but omits here is the failure mode worth pinning: the
// merge helper then re-sends the value already on the server, so the toggle
// moves, the save succeeds, and the setting never changes.
export function endpointConfigOverlay(config) {
    return {
        hide_tray_icon: config.hide_tray_icon,
        otel_collector_port: config.otel_collector_port,
    };
}

function flattenConfig(c) {
    if (!c) return defaultEndpointConfig();
    return {
        hide_tray_icon: c.hideTrayIcon ?? c.hide_tray_icon ?? false,
        otel_collector_port: readOtelCollectorPort(c),
    };
}

/** Text shown in the port input for a flattened config value. */
function otelPortText(port) {
    return port == null ? "" : String(port);
}

// On every endpoint, in Settings' Endpoints group. The tray icon is a switch
// that saves at once; the port is typed, so it saves when asked. Each says
// when endpoints pick the change up.
export function EndpointConfigurationSection() {
    const [saved, setSaved] = useState(defaultEndpointConfig);
    const [portText, setPortText] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [busy, setBusy] = useState(null);
    // The last save's outcome, by the control it came from: { field, error }.
    const [result, setResult] = useState(null);
    const portParse = useMemo(() => parseOtelCollectorPortInput(portText), [portText]);
    const portChanged = portText !== otelPortText(saved.otel_collector_port);
    const canSavePort = portChanged && portParse.ok && !busy && !loading;

    const refetch = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const resp = await restGet("/v1/tenant/configuration");
            const next = flattenConfig(resp?.configuration);
            setSaved(next);
            setPortText(otelPortText(next.otel_collector_port));
        } catch (err) {
            console.error("[settings] tenant config", err);
            setLoadError(err.message || String(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refetch().catch(() => {}); }, [refetch]);

    const save = useCallback(async (next, field) => {
        setBusy(field);
        setResult(null);
        try {
            const json = await saveTenantConfiguration(endpointConfigOverlay(next));
            const confirmed = flattenConfig(json?.configuration ?? next);
            setSaved(confirmed);
            if (field === "port") setPortText(otelPortText(confirmed.otel_collector_port));
            setResult({ field, error: null });
        } catch (err) {
            setResult({ field, error: err.message || String(err) });
        } finally {
            setBusy(null);
        }
    }, []);

    const statusOf = (field) => (busy === field ? "saving" : result?.field === field && !result.error ? "saved" : null);
    const errorOf = (field) => (result?.field === field ? result.error : null);

    return (
        <div id="settings-endpoint-config" aria-busy={loading || busy ? "true" : undefined} className="grid max-w-[640px] gap-[22px]">
            {loadError ? (
                <Notice tone="error" title="Couldn't load the sensor configuration" action={<Button size="sm" onClick={() => void refetch()}>Retry</Button>}>
                    {loadError}
                </Notice>
            ) : null}
            <div className="flex items-start justify-between gap-[16px]">
                <div className="grid min-w-0 gap-[3px]">
                    <label htmlFor="settings-hide-tray-icon" className="text-[13px] font-medium leading-[1.3] text-[color:var(--ink)]">Hide the system tray icon</label>
                    <span className="text-[12px] leading-[1.4] text-[color:var(--muted)]">Each endpoint picks this up at its next check-in.</span>
                </div>
                <div className="flex shrink-0 items-center gap-[10px] pt-[1px]">
                    <SaveStatus status={statusOf("tray")} error={errorOf("tray")} />
                    <Switch
                        id="settings-hide-tray-icon"
                        checked={saved.hide_tray_icon}
                        disabled={loading || Boolean(busy)}
                        label="Hide the system tray icon"
                        onChange={(value) => save({ ...saved, hide_tray_icon: value }, "tray")}
                    />
                </div>
            </div>
            <form
                className="grid gap-[12px]"
                onSubmit={(e) => {
                    e.preventDefault();
                    if (canSavePort) save({ ...saved, otel_collector_port: portParse.value }, "port");
                }}
            >
                {/* The field, its Save beside it, and the save under way beside that. */}
                <div className="grid gap-[6px]">
                    <label htmlFor="settings-otel-port" className="text-[12.5px] font-medium leading-[1.3] text-[color:var(--ink)]">Local collector port</label>
                    <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[8px]">
                        <input
                            id="settings-otel-port"
                            type="text"
                            inputMode="numeric"
                            value={portText}
                            onChange={(e) => { setPortText(e.target.value); setResult((r) => (r?.field === "port" ? null : r)); }}
                            placeholder={`${DEFAULT_OTEL_COLLECTOR_PORT}`}
                            aria-invalid={!portParse.ok}
                            aria-describedby="settings-otel-port-note"
                            className={`${FIELD} w-[120px] font-mono`}
                        />
                        <Button type="submit" variant={canSavePort ? "primary" : "secondary"} disabled={!canSavePort}>Save port</Button>
                        <SaveStatus status={statusOf("port")} error={errorOf("port")} />
                    </div>
                    <div id="settings-otel-port-note">
                        {portParse.ok ? (
                            <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">
                                Endpoints switch to a new port when Origin&apos;s sensor restarts. On macOS, capture pauses briefly while it reconnects.
                            </p>
                        ) : <ErrorText>{portParse.error}</ErrorText>}
                    </div>
                </div>
            </form>
        </div>
    );
}

// ── Password Management ──────────────────────────────────────────

// User settings' password form; kept importable from here for older callers.
import PasswordSection from "../components/settings/PasswordSection.jsx";
export { PasswordSection };

// ── Page ─────────────────────────────────────────────────────────

// Anthropic key (O-1700). Admin-only. The key is write-only: the GET returns a
// "configured / not configured" status, never the secret, so we never
// round-trip the value back into the input.
//
// The route, the key's name and the API's name stay "compliance": they are
// Anthropic's own spelling and the admin has to find them in claude.ai.
function AnthropicApiKeySectionContent({ showPermissionUnavailable = false }) {
    const caps = useMyPermissions();
    const [apiKey, setApiKey] = useState("");
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const {
        authorized,
        integration: summary,
        requestStatus,
        requestError,
        refresh,
    } = useComplianceIntegrationHealth();

    const onSave = useCallback(async () => {
        setStatus("saving");
        setError(null);
        try {
            await restPost("/v1/compliance-integration:set-key", {
                api_key: apiKey.trim(),
            });
            setApiKey("");
            setStatus("saved");
            await refresh({ afterCurrent: true });
            setTimeout(() => setStatus(null), 1800);
        } catch (err) {
            setStatus(null);
            setError(denialOrRawMessage(err, caps));
        }
    }, [apiKey, refresh, caps]);

    const onRemove = useCallback(async () => {
        setStatus("saving");
        setError(null);
        try {
            await restDelete("/v1/compliance-integration");
            setApiKey("");
            await refresh({ afterCurrent: true });
            setStatus(null);
        } catch (err) {
            setStatus(null);
            setError(denialOrRawMessage(err, caps));
        }
    }, [refresh, caps]);

    // The RPC remains fail-closed while permission verification is degraded.
    // A mixed-permission page can opt into bounded posture copy without
    // exposing the key controls before the admin-equivalent grant is known.
    if (!authorized) {
        const permissionPending = !caps || caps.loading;
        const permissionUnavailable = caps?.degraded || caps?.subjectScopeDegraded;
        if (!showPermissionUnavailable || (!permissionPending && !permissionUnavailable)) return null;
        return (
            <SectionCard
                id="settings-compliance-api-key"
                eyebrow="Integrations"
                title="Anthropic Compliance API key"
                description="Import claude.ai and Claude Desktop chats through Anthropic's Compliance API."
            >
                <div aria-busy={permissionPending ? "true" : undefined}>
                    <p className={NOTE_CLASS} role="status">
                        {permissionPending
                            ? "Checking whether your account can manage Anthropic capture."
                            : "Anthropic settings are temporarily unavailable while Origin verifies your permissions."}
                    </p>
                </div>
            </SectionCard>
        );
    }

    const canSubmit = apiKey.trim().length > 0 && status !== "saving";
    const configured = summary?.configured ?? false;
    const loading = (requestStatus === "idle" || requestStatus === "loading") && !summary;
    const health = complianceHealthPresentation(summary);
    const healthTone = health.tone === "critical"
        ? "border-red-500/50 bg-red-500/10"
        : health.tone === "warning"
            ? "border-amber-500/50 bg-amber-500/10"
            : health.tone === "healthy"
                ? "border-[color:var(--line)] bg-[color:var(--control-bg)]"
                : "border-[color:var(--line)] bg-[color:var(--control-bg)]";

    return (
        <SectionCard
            id="settings-anthropic-api-key"
            eyebrow="Integrations"
            title="Anthropic (claude.ai and Claude Desktop)"
            description="Import the chats your people have in claude.ai and Claude Desktop, so they appear alongside the prompts captured on the endpoint. Paste a Compliance Access Key (sk-ant-api01-...) created in claude.ai."
            footer={
                <div className="flex items-center justify-between gap-[10px]">
                    <StatusLine status={status} error={error} savedHint="Key saved" />
                    <div className="flex items-center gap-[8px]">
                        {configured ? (
                            <button type="button" onClick={onRemove} className={secondaryBtnCls}>
                                Remove
                            </button>
                        ) : null}
                        <button
                            type="button"
                            onClick={onSave}
                            disabled={!canSubmit}
                            className={canSubmit ? primaryBtnCls : secondaryBtnCls}
                        >
                            {configured ? "Replace key" : "Save key"}
                        </button>
                    </div>
                </div>
            }
        >
            <div aria-busy={loading ? "true" : undefined}>
                {loading ? (
                    <p className={NOTE_CLASS}>Loading...</p>
                ) : (
                    <>
                        {requestStatus === "unavailable" ? (
                            <p className={`${NOTE_CLASS} mb-[10px]`} role="status">
                                {requestError || "Integration status is temporarily unavailable."}
                                {summary ? " The last confirmed connector status is shown below." : ""}
                            </p>
                        ) : null}
                        {requestStatus === "unavailable" && !summary ? null : (
                            <div
                                data-compliance-health-status={summary?.healthStatus ?? "unconfigured"}
                                className={`mb-[12px] grid gap-[8px] rounded-[10px] border px-[10px] py-[9px] ${healthTone}`}
                                aria-live="polite"
                            >
                                <div className="flex items-baseline justify-between gap-[12px]">
                                    <strong className="flex items-center gap-[6px] text-[12px] font-medium text-[color:var(--ink)]">
                                        {health.tone === "healthy" ? (
                                            <span className="h-[5px] w-[5px] rounded-full bg-emerald-500/70" aria-hidden="true" />
                                        ) : null}
                                        {health.label}
                                    </strong>
                                    {requestStatus === "refreshing" ? (
                                        <span className={`${NOTE_CLASS} text-[10px]`}>Refreshing...</span>
                                    ) : null}
                                </div>
                                <p className={`${NOTE_CLASS} text-[11px] leading-[1.4]`}>{health.impact}</p>
                                {configured ? (
                                    <dl className="grid grid-cols-1 gap-[5px] text-[12px] text-[color:var(--muted)] sm:grid-cols-3">
                                        <div>
                                            <dt>Last check</dt>
                                            <dd className="mt-[1px] text-[color:var(--ink)]">{formatComplianceHealthTime(summary.lastCheckedAt)}</dd>
                                        </div>
                                        <div>
                                            <dt>Last success</dt>
                                            <dd className="mt-[1px] text-[color:var(--ink)]">{formatComplianceHealthTime(summary.lastSuccessAt)}</dd>
                                        </div>
                                        <div>
                                            <dt>Last failure</dt>
                                            <dd className="mt-[1px] text-[color:var(--ink)]">{formatComplianceHealthTime(summary.lastErrorAt)}</dd>
                                        </div>
                                    </dl>
                                ) : null}
                            </div>
                        )}
                        <FormField
                            label="Anthropic Compliance Access Key"
                            hint="Created in claude.ai. Stored encrypted; never shown again after saving."
                        >
                            <input
                                type="password"
                                value={apiKey}
                                autoComplete="off"
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder="sk-ant-..."
                                className={inputCls}
                            />
                        </FormField>
                    </>
                )}
            </div>
        </SectionCard>
    );
}

export function AnthropicApiKeySection({ showPermissionUnavailable = false }) {
    return (
        <ComplianceIntegrationHealthProvider>
            <AnthropicApiKeySectionContent showPermissionUnavailable={showPermissionUnavailable} />
        </ComplianceIntegrationHealthProvider>
    );
}

function CopyValue({ label, value }) {
    const [copied, setCopied] = useState(false);
    const copy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {}
    }, [value]);

    return (
        <FormField label={label}>
            <div className="flex gap-[8px]">
                <input
                    aria-label={label}
                    type="text"
                    readOnly
                    value={value}
                    className={`${inputCls} min-w-0 flex-1 font-mono`}
                />
                <button type="button" onClick={copy} className={secondaryBtnCls}>
                    {copied ? "Copied" : "Copy"}
                </button>
            </div>
        </FormField>
    );
}

export function PerplexityAuditLogSection() {
    const caps = useMyPermissions();
    const capsRef = useLatestRef(caps);
    const [summary, setSummary] = useState(null);
    const [oneTimeCredential, setOneTimeCredential] = useState(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(false);
    const refetch = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setSummary(perplexitySummary(await getPerplexityAuditIntegration()));
        } catch (err) {
            setError(denialCopy(err, capsRef.current).message);
        } finally {
            setLoading(false);
        }
    }, [capsRef]);

    useEffect(() => {
        refetch();
    }, [refetch]);

    const saveResponse = useCallback((response) => {
        const next = perplexitySummary(response);
        setSummary(next);
        setOneTimeCredential(
            next.webhookToken
                ? { webhookUrl: next.webhookUrl, webhookToken: next.webhookToken }
                : null,
        );
    }, []);

    const create = useCallback(async () => {
        setStatus("saving");
        setError(null);
        try {
            saveResponse(await createPerplexityAuditIntegration());
            setStatus("saved");
        } catch (err) {
            setStatus(null);
            setError(denialOrRawMessage(err, caps));
        }
    }, [caps, saveResponse]);

    const rotate = useCallback(async () => {
        setStatus("saving");
        setError(null);
        try {
            saveResponse(await rotatePerplexityAuditCredential());
            setStatus("saved");
        } catch (err) {
            setStatus(null);
            setError(denialOrRawMessage(err, caps));
        }
    }, [caps, saveResponse]);

    const remove = useCallback(async () => {
        setConfirmRemove(false);
        setStatus("saving");
        setError(null);
        try {
            await deletePerplexityAuditIntegration();
            setOneTimeCredential(null);
            setSummary(perplexitySummary(null));
            setStatus(null);
        } catch (err) {
            setStatus(null);
            setError(denialOrRawMessage(err, caps));
        }
    }, [caps]);

    const configured = summary?.configured ?? false;
    const busy = status === "saving";

    return (
        <SectionCard
            id="settings-perplexity-audit-log"
            eyebrow="Integrations"
            title="Perplexity Enterprise Audit Logs"
            description="Import Perplexity prompts, responses, and automated agent actions through an authenticated webhook. Create the webhook here, then paste its URL and bearer token into the Perplexity admin console."
            footer={
                <div className="flex items-center justify-between gap-[10px]">
                    <StatusLine status={status} error={error} savedHint="Webhook ready" />
                    <div className="flex items-center gap-[8px]">
                        {configured ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setConfirmRemove(true)}
                                    disabled={busy}
                                    className={secondaryBtnCls}
                                >
                                    Remove
                                </button>
                                <button
                                    type="button"
                                    onClick={rotate}
                                    disabled={busy}
                                    className={secondaryBtnCls}
                                >
                                    Rotate token
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={create}
                                disabled={busy || loading}
                                className={primaryBtnCls}
                            >
                                Create webhook
                            </button>
                        )}
                    </div>
                </div>
            }
        >
            <div aria-busy={loading ? "true" : undefined} className="grid gap-[14px]">
                {loading && !summary ? (
                    <p className={NOTE_CLASS}>Loading...</p>
                ) : error && !summary ? (
                    <div className="grid gap-[8px] justify-items-start">
                        <p className={NOTE_CLASS}>Could not load the Perplexity connection.</p>
                        <button type="button" onClick={refetch} className={secondaryBtnCls}>
                            Retry
                        </button>
                    </div>
                ) : configured ? (
                    <>
                        <p className={NOTE_CLASS}>
                            {summary.status ? `Status: ${summary.status}.` : "Connected."} Authorized audit
                            payloads are retained for {summary.rawRetentionDays} days.
                        </p>
                        <CopyValue label="Webhook URL" value={summary.webhookUrl} />
                        {oneTimeCredential ? (
                            <div className="grid gap-[10px] rounded-[10px] border border-[color:var(--line)] bg-[color-mix(in_srgb,var(--ink)_5%,transparent)] p-[10px]">
                                <p className="m-0 text-[12px] leading-[1.4] text-[color:var(--ink)]">
                                    Copy this bearer token now. Origin will not show it again.
                                </p>
                                <CopyValue
                                    label="Bearer token"
                                    value={oneTimeCredential.webhookToken}
                                />
                                <p className={NOTE_CLASS}>
                                    In Perplexity, use the webhook URL above and send this value as
                                    the Authorization header: Bearer &lt;token&gt;.
                                </p>
                            </div>
                        ) : null}
                        {summary.previousCredentialValidUntil ? (
                            <p className={NOTE_CLASS}>
                                The previous token remains valid until {new Date(
                                    summary.previousCredentialValidUntil,
                                ).toLocaleString()}.
                            </p>
                        ) : null}
                    </>
                ) : (
                    <p className={NOTE_CLASS}>
                        Not configured. Creating a webhook generates a URL and one-time bearer
                        token for the Perplexity admin console.
                    </p>
                )}
            </div>
            <ConfirmDialog
                open={confirmRemove}
                analyticsId={DIALOG.PERPLEXITY_WEBHOOK_REMOVE}
                eyebrow="Perplexity Audit Logs"
                title="Remove the Perplexity webhook?"
                body="Perplexity deliveries will stop immediately. Previously retained audit events are not deleted."
                confirmLabel="Remove webhook"
                destructive
                onConfirm={remove}
                onCancel={() => setConfirmRemove(false)}
            />
        </SectionCard>
    );
}

function langsmithStatusLabel(status) {
    switch (status) {
        case "connected":
            return "Connected.";
        case "pending":
            return "Waiting on its first delivery.";
        case "error":
            return "Not capturing.";
        case "disabled":
            return "Disabled.";
        default:
            return "";
    }
}

export function LangSmithSection() {
    const caps = useMyPermissions();
    const capsRef = useLatestRef(caps);
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [apiKey, setApiKey] = useState("");
    const [workspaceId, setWorkspaceId] = useState("");
    const [status, setStatus] = useState(null);
    const [openId, setOpenId] = useState(null);
    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [projectsError, setProjectsError] = useState(null);
    const [selection, setSelection] = useState(() => new Set());
    const [projectStatus, setProjectStatus] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(null);
    const openRequest = useRef(null);

    const refetch = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const response = await listLangSmithIntegrations();
            setIntegrations((response?.integrations ?? []).map(langsmithIntegration));
        } catch (err) {
            setLoadError(denialCopy(err, capsRef.current).message);
        } finally {
            setLoading(false);
            setLoaded(true);
        }
    }, [capsRef]);

    useEffect(() => {
        refetch();
    }, [refetch]);

    const openPicker = useCallback(
        async (integration) => {
            if (openId === integration.id) {
                openRequest.current = null;
                setOpenId(null);
                return;
            }
            openRequest.current = integration.id;
            setOpenId(integration.id);
            setProjects([]);
            setProjectStatus(null);
            setProjectsError(null);
            setProjectsLoading(true);
            setSelection(new Set(integration.watchedProjects.map((project) => project.projectId)));
            try {
                const response = await listLangSmithProjects(integration.id);
                if (openRequest.current !== integration.id) return;
                setProjects(
                    (response?.projects ?? []).map((project) => ({
                        projectId: project?.projectId ?? project?.project_id ?? "",
                        displayName: project?.displayName ?? project?.display_name ?? "",
                    })),
                );
            } catch (err) {
                if (openRequest.current !== integration.id) return;
                setProjectsError(denialOrRawMessage(err, caps));
            } finally {
                if (openRequest.current === integration.id) setProjectsLoading(false);
            }
        },
        [caps, openId],
    );

    const create = useCallback(async () => {
        setStatus("saving");
        setError(null);
        try {
            await createLangSmithIntegration({
                apiKey: apiKey.trim(),
                workspaceId: workspaceId.trim(),
            });
            setApiKey("");
            setWorkspaceId("");
            setStatus("saved");
            await refetch();
            setTimeout(() => setStatus(null), 1800);
        } catch (err) {
            setStatus(null);
            setError(denialOrRawMessage(err, caps));
        }
    }, [apiKey, caps, refetch, workspaceId]);

    const saveSelection = useCallback(
        async (integrationId) => {
            setProjectStatus("saving");
            setProjectsError(null);
            try {
                await setLangSmithWatchedProjects(integrationId, [...selection]);
                setProjectStatus("saved");
                await refetch();
                setTimeout(() => setProjectStatus(null), 1800);
            } catch (err) {
                setProjectStatus(null);
                setProjectsError(denialOrRawMessage(err, caps));
            }
        },
        [caps, refetch, selection],
    );

    const remove = useCallback(async () => {
        const target = confirmRemove;
        setConfirmRemove(null);
        if (!target) return;
        setStatus("saving");
        setError(null);
        try {
            await deleteLangSmithIntegration(target.id);
            if (openId === target.id) setOpenId(null);
            await refetch();
            setStatus(null);
        } catch (err) {
            setStatus(null);
            setError(denialOrRawMessage(err, caps));
        }
    }, [caps, confirmRemove, openId, refetch]);

    const toggleProject = useCallback((projectId) => {
        setSelection((current) => {
            const next = new Set(current);
            if (next.has(projectId)) next.delete(projectId);
            else next.add(projectId);
            return next;
        });
    }, []);

    const canCreate =
        apiKey.trim().length > 0 && workspaceId.trim().length > 0 && status !== "saving";

    return (
        <SectionCard
            id="settings-langsmith"
            eyebrow="Integrations"
            title="LangSmith (LangChain and LangGraph agents)"
            description="Capture prompts from agents your own teams build. Paste a workspace-scoped LangSmith service key, then pick which tracing projects to watch. Origin puts an automation rule on each project you pick, and LangSmith posts new runs to us as they finish."
            footer={
                <div className="flex items-center justify-between gap-[10px]">
                    <StatusLine status={status} error={error} savedHint="Workspace connected" />
                    <button
                        type="button"
                        onClick={create}
                        disabled={!canCreate}
                        className={canCreate ? primaryBtnCls : secondaryBtnCls}
                    >
                        Connect workspace
                    </button>
                </div>
            }
        >
            <div aria-busy={loading ? "true" : undefined} className="grid gap-[14px]">
                <div className="grid gap-[10px] sm:grid-cols-2">
                    <FormField
                        label="Service key"
                        hint="Scope it to one workspace. It needs to read runs and projects, create automation rules, and read organization members. Stored encrypted and never shown again."
                    >
                        <input
                            type="password"
                            value={apiKey}
                            autoComplete="off"
                            onChange={(event) => setApiKey(event.target.value)}
                            placeholder="lsv2_sk_…"
                            className={inputCls}
                        />
                    </FormField>
                    <FormField
                        label="Workspace ID"
                        hint="LangSmith shows this under Settings, Workspaces. The key must belong to this workspace or connecting fails."
                    >
                        <input
                            type="text"
                            value={workspaceId}
                            onChange={(event) => setWorkspaceId(event.target.value)}
                            placeholder="00000000-0000-0000-0000-000000000000"
                            className={inputCls}
                        />
                    </FormField>
                </div>
                <div className="grid gap-[10px] border-t border-[color:var(--line)] pt-[12px]">
                    <h3 className="m-0 text-[13px] font-medium">Connected workspaces</h3>
                    {loading && !loaded ? (
                        <p className={NOTE_CLASS}>Loading…</p>
                    ) : loadError && integrations.length === 0 ? (
                        <div className="grid gap-[8px] justify-items-start">
                            <p className={NOTE_CLASS}>Could not load LangSmith workspaces.</p>
                            <button type="button" onClick={refetch} className={secondaryBtnCls}>
                                Retry
                            </button>
                        </div>
                    ) : integrations.length === 0 ? (
                        <p className={NOTE_CLASS}>
                            None yet. A key reaches one LangSmith workspace, so connect each
                            workspace whose agents you want captured.
                        </p>
                    ) : (
                        integrations.map((integration) => {
                            const watchedById = new Map(
                                integration.watchedProjects.map((project) => [
                                    project.projectId,
                                    project,
                                ]),
                            );
                            const open = openId === integration.id;
                            const lastPoll = whenLabel(integration.lastPollAt);
                            return (
                                <div
                                    key={integration.id}
                                    className="grid gap-[8px] rounded-[10px] border border-[color:var(--line)] p-[10px]"
                                >
                                    <div className="flex flex-wrap items-baseline justify-between gap-[8px]">
                                        <span className="font-mono text-[12px] text-[color:var(--ink)]">
                                            {integration.workspaceId}
                                        </span>
                                        <div className="flex items-center gap-[8px]">
                                            <button
                                                type="button"
                                                onClick={() => openPicker(integration)}
                                                className={secondaryBtnCls}
                                            >
                                                {open ? "Close" : "Choose projects"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setConfirmRemove(integration)}
                                                className={secondaryBtnCls}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                    <p className={NOTE_CLASS}>
                                        {langsmithStatusLabel(integration.status)
                                            ? `${langsmithStatusLabel(integration.status)} `
                                            : ""}
                                        {integration.watchedProjects.length === 0
                                            ? "No projects watched, so nothing is being captured from this workspace."
                                            : `${integration.watchedProjects.length} project${
                                                  integration.watchedProjects.length === 1 ? "" : "s"
                                              } watched.`}
                                        {lastPoll
                                            ? ` Last delivery ${lastPoll}.`
                                            : " No deliveries yet."}
                                    </p>
                                    {integration.lastSyncError ? (
                                        <p className="m-0 text-[11px] text-red-400">
                                            {integration.lastSyncError}
                                        </p>
                                    ) : null}
                                    {open ? (
                                        <div
                                            aria-busy={projectsLoading ? "true" : undefined}
                                            className="grid gap-[10px] border-t border-[color:var(--line)] pt-[10px]"
                                        >
                                            {projectsLoading ? (
                                                <p className={NOTE_CLASS}>Reading the workspace…</p>
                                            ) : projectsError && projects.length === 0 ? (
                                                <p className="m-0 text-[11px] text-red-400">
                                                    {projectsError}
                                                </p>
                                            ) : projects.length === 0 ? (
                                                <p className={NOTE_CLASS}>
                                                    This workspace has no tracing projects yet. One
                                                    appears here once an agent traces into it.
                                                </p>
                                            ) : (
                                                <>
                                                    {projects.map((project) => {
                                                        const watched = watchedById.get(
                                                            project.projectId,
                                                        );
                                                        const lastRun = whenLabel(
                                                            watched?.lastWindowEnd,
                                                        );
                                                        return (
                                                            <Checkbox
                                                                key={project.projectId}
                                                                checked={selection.has(
                                                                    project.projectId,
                                                                )}
                                                                onChange={() =>
                                                                    toggleProject(
                                                                        project.projectId,
                                                                    )
                                                                }
                                                                label={<span className="grid gap-[2px] text-[12px]">
                                                                    <span>
                                                                        {project.displayName ||
                                                                            project.projectId}
                                                                    </span>
                                                                    {watched ? (
                                                                        <span className={NOTE_CLASS}>
                                                                            {lastRun
                                                                                ? `Runs received up to ${lastRun}.`
                                                                                : "Watched. Nothing delivered yet."}
                                                                            {watched.ruleId
                                                                                ? ` Rule ${watched.ruleId}.`
                                                                                : ""}
                                                                        </span>
                                                                    ) : null}
                                                                </span>}
                                                            />
                                                        );
                                                    })}
                                                    <p className={NOTE_CLASS}>
                                                        Unticking a project deletes its automation
                                                        rule in LangSmith and stops capture for it.
                                                        Runs already captured stay.
                                                    </p>
                                                    <div className="flex items-center justify-between gap-[10px]">
                                                        <StatusLine
                                                            status={projectStatus}
                                                            error={projectsError}
                                                            savedHint="Projects updated"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                saveSelection(integration.id)
                                                            }
                                                            disabled={projectStatus === "saving"}
                                                            className={primaryBtnCls}
                                                        >
                                                            Save projects
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
            <ConfirmDialog
                open={Boolean(confirmRemove)}
                analyticsId={DIALOG.LANGSMITH_DISCONNECT}
                eyebrow="LangSmith"
                title="Disconnect this workspace?"
                body="Every automation rule Origin created in this workspace is deleted and capture stops. Runs already captured are kept. Reconnecting means pasting the key again and picking the projects again."
                confirmLabel="Disconnect"
                destructive
                onConfirm={remove}
                onCancel={() => setConfirmRemove(null)}
            />
        </SectionCard>
    );
}

export default function SettingsPage({ viewDef }) {
    // Each section is gated on the permission its writes require — not a
    // role name (custom roles arrive in RBAC v2; name-gating would
    // break). Tenant-config sections (PUT /v1/tenant/configuration) need
    // tenant_config.write. PasswordSection / ClearLocalData are per-user,
    // always shown. Gates fail open on a pre-RBAC tenant, preserving prior UI.
    const caps = useMyPermissions();
    const canWriteTenantConfig = hasPermission(caps, PERMISSIONS.TENANT_CONFIG_WRITE);
    // These two key-management RPC families still use the transitional
    // require_admin() shim, whose exact ABAC gate is tenant.users.manage.
    // Keep them separate from tenant-configuration writes so a custom role
    // never sees controls the backend will categorically reject.
    const canManageAdminIntegrations = hasPermission(caps, PERMISSIONS.USERS_MANAGE);
    return (
        <>
            <AdminViewHeader title={viewDef?.label ?? "Settings"} />
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto py-[12px]">
                <div className="grid gap-[14px]">
                    {/* The signed-in user's own scope (informational). Renders
                        nothing on a pre-RBAC tenant, so it's safe to keep at
                        the top of the always-visible content. */}
                    <AccessSummary />
                    {canWriteTenantConfig ? (
                        <>
                            <EndpointConfigurationSection />
                        </>
                    ) : null}
                    {canManageAdminIntegrations ? (
                        <>
                            <AnthropicApiKeySection showPermissionUnavailable />
                            <PerplexityAuditLogSection />
                        </>
                    ) : null}
                    <PasswordSection />
                </div>
            </div>
        </>
    );
}
